/**
 * `<LeadersPanel>` — the Leaders panel's top half (`docs/plans/leader-screener.md`
 * step 5, mock `docs/leader-screener-mock.html`).
 *
 * Drawn by `ScreenerView` above **any** screen's grid when ▦ Leaders is on:
 * the bar (GR ≥, As of, Grid | Pie), the growth boxes or the pie, and the
 * Session card. The grid under it is the open screen, narrowed to the leaders
 * in the selection (`lib/leaders-panel.ts` `applyPanel`). Presentational: the
 * state and the fetch are `ScreenerView`'s.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { useTheme, useTokenResolver } from "./theme";

import {
  BOX_ORDER,
  boxLabel,
  boxPaints,
  boxStats,
  clampThreshold,
  GRID_ROWS,
  pickBox,
  pieSlices,
  SIDE_BOXES,
  stepWeekday,
  type BoxPaint,
  type BoxStat,
  type LeadersView,
} from "../lib/leaders-panel";

export interface LeadersSettings {
  threshold: number;
  view: "boxes" | "pie";
  /** `YYYY-MM-DD`, or null for Latest. */
  date: string | null;
  selection: ReadonlySet<number>;
}

export interface LeadersPanelProps {
  settings: LeadersSettings;
  onChange: (next: LeadersSettings) => void;
  view: LeadersView | null;
  /** The session the answer is for, and the one before it. */
  resolvedDate: string | null;
  priorDate: string | null;
  /** Symbols the screen holds — the universe the leaders are drawn from. */
  universe: number;
  loading: boolean;
  error: string | null;
  /** Today, `YYYY-MM-DD`: the date picker's ceiling. */
  today: string;
}

/** The earliest date offered: the rank artifact's warm-up starts in 2020 (plan O-9). */
export const EARLIEST = "2021-01-04";

/**
 * Box colours: the theme's `positive` for box 1, lighter shades to box 9, 10
 * and 0 light grey, and the text chosen per box for contrast (`boxPaints`) — a
 * navy positive needs white on its first boxes (owner, 11-03).
 */
function useBoxPaints(): (box: number) => BoxPaint {
  const resolve = useTokenResolver();
  return useMemo(() => boxPaints(resolve("positive")), [resolve]);
}

export function LeadersPanel({
  settings, onChange, view, resolvedDate, priorDate, universe, loading, error, today,
}: LeadersPanelProps) {
  const set = (patch: Partial<LeadersSettings>) => onChange({ ...settings, ...patch });
  const theme = useTheme();
  const dateRef = useRef<HTMLInputElement | null>(null);
  // A click anywhere on the box, or on 📅, opens the calendar — ChartToolbar's
  // Go to date rule: some browsers open it only from the input's own glyph.
  const openCalendar = () => {
    try {
      dateRef.current?.showPicker?.();
    } catch {
      // No user activation, or unsupported: the input's own glyph still works.
    }
  };
  const stats = boxStats(view?.leaders ?? []);
  const pick = (box: number, additive: boolean) => set({ selection: pickBox(settings.selection, box, additive) });
  const shownDate = settings.date ?? resolvedDate ?? today;
  const step = (dir: 1 | -1) => {
    const next = stepWeekday(shownDate, dir);
    if (next < EARLIEST) return;
    set({ date: next >= today ? null : next });
  };

  return (
    <div style={ROOT} data-leaders-panel>
      <div style={BAR} role="toolbar" aria-label="Boxes">
        <span style={LBL}>GR ≥</span>
        <ThresholdBox value={settings.threshold} onChange={(threshold) => set({ threshold })} />
        <span style={SEP} />
        <span style={LBL}>As of</span>
        <button type="button" style={BTN} aria-label="Previous session" onClick={() => step(-1)}>◀</button>
        <input
          ref={dateRef}
          type="date"
          // The theme's mode, so the native calendar glyph draws in both themes.
          style={{ ...INPUT, colorScheme: theme.mode, cursor: "pointer" }}
          aria-label="As of date"
          onClick={openCalendar}
          min={EARLIEST}
          max={today}
          value={shownDate}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return;
            set({ date: v >= today ? null : v < EARLIEST ? EARLIEST : v });
          }}
        />
        <button type="button" style={BTN} aria-label="Open the calendar" title="Pick a date" onClick={openCalendar}>
          📅
        </button>
        <button type="button" style={BTN} aria-label="Next session" disabled={settings.date === null} onClick={() => step(1)}>▶</button>
        <button type="button" style={settings.date === null ? { ...BTN, ...ON } : BTN} onClick={() => set({ date: null })}>
          Latest
        </button>
        {settings.date !== null && resolvedDate && (
          <span style={HIST} title="The panel's columns are as of this date; every other column is today">
            ◷ as of {resolvedDate}
          </span>
        )}
        <span style={SEP} />
        <span style={SEG}>
          {(["boxes", "pie"] as const).map((v, i) => (
            <button key={v} type="button" aria-pressed={settings.view === v} onClick={() => set({ view: v })}
              style={{ ...SEG_BTN, ...(i ? SEG_SEP : {}), ...(settings.view === v ? SEG_ON : {}) }}>
              {v === "boxes" ? "Grid" : "Pie"}
            </button>
          ))}
        </span>
        {settings.selection.size > 0 && (
          <button type="button" style={CRUMB} onClick={() => set({ selection: new Set() })} title="Show every box">
            {[...settings.selection].sort((a, b) => a - b).map(boxLabel).join(", ")} ✕
          </button>
        )}
      </div>
      <div style={TOP}>
        {error ? (
          <div style={NOTE}>Boxes: {error}</div>
        ) : !view ? (
          <div style={NOTE}>{loading ? "Ranking…" : "Waiting for the screen's rows…"}</div>
        ) : (
          <>
            {settings.view === "boxes" ? (
              <Boxes stats={stats} selection={settings.selection} onPick={pick} threshold={settings.threshold} />
            ) : (
              <Pie stats={stats} selection={settings.selection} onPick={pick} threshold={settings.threshold} total={view.leaders.length} />
            )}
            <div style={CARD}>
              <div style={CARD_HEAD}>Session</div>
              <div>As of <b>{resolvedDate ?? "—"}</b>{loading ? " · updating…" : ""}</div>
              <div>GR ≥ {settings.threshold}: <b>{view.leaders.length.toLocaleString()}</b> of {universe.toLocaleString()} on this screen</div>
              {view.hasPrior && priorDate ? (
                <div title={`Against ${priorDate}`}>
                  vs prior session: <b style={{ color: "var(--qf-positive)" }}>+{view.entered}</b> in,{" "}
                  <b style={{ color: "var(--qf-negative)" }}>−{view.left.length}</b> out
                </div>
              ) : null}
              <div>Boxes 1–5: <b>{[1, 2, 3, 4, 5].reduce((s, b) => s + (stats.get(b)?.count ?? 0), 0)}</b></div>
              <div>No Box: <b>{stats.get(0)?.count ?? 0}</b></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ThresholdBox({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  // Applied on commit — Enter or leaving the box — never per keystroke: the
  // threshold filters rows already held, so typing "2" on the way to "240"
  // would otherwise flash the whole screen through.
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const commit = () => {
    const next = clampThreshold(Number(text));
    setText(String(next));
    if (next !== value) onChange(next);
  };
  return (
    <input type="number" aria-label="GR threshold" min={0} max={297} step={10} value={text}
      style={{ ...INPUT, width: 62 }} onChange={(e) => setText(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }} />
  );
}

function BoxCell({ box, stat, on, onPick, paint }: { box: number; stat: BoxStat | undefined; on: boolean; onPick: (box: number, additive: boolean) => void; paint: BoxPaint }) {
  const count = stat?.count ?? 0;
  const text = paint.text;
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={`${boxLabel(box)}: ${count}`}
      onClick={(e) => onPick(box, e.shiftKey || e.ctrlKey || e.metaKey)}
      style={{
        ...CELL,
        background: paint.fill,
        color: text,
        opacity: count ? 1 : 0.35,
        outline: on ? "2px solid var(--qf-accent)" : undefined,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.7 }}>{box === 0 ? "0 · No Box" : `Box ${box}`}</span>
      <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{count}</span>
      <span style={{ fontSize: 10.5, opacity: 0.75 }}>{stat?.avgErg != null ? `avg GR ${stat.avgErg}` : " "}</span>
    </button>
  );
}

function Boxes({ stats, selection, onPick, threshold }: {
  stats: Map<number, BoxStat>; selection: ReadonlySet<number>; onPick: (box: number, additive: boolean) => void; threshold: number;
}) {
  const paints = useBoxPaints();
  return (
    <div>
      <div style={CARD_HEAD}>Growth boxes · GR ≥ {threshold}</div>
      <div style={GRID}>
        {GRID_ROWS.map((row, r) => (
          <div key={r} style={{ display: "contents" }}>
            {row.map((b) => (
              <BoxCell key={b} box={b} stat={stats.get(b)} on={selection.has(b)} onPick={onPick} paint={paints(b)} />
            ))}
            <span />
            {r === 0 && (
              <div style={SIDE}>
                {SIDE_BOXES.map((b) => (
                  <BoxCell key={b} box={b} stat={stats.get(b)} on={selection.has(b)} onPick={onPick} paint={paints(b)} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Pie({ stats, selection, onPick, threshold, total }: {
  stats: Map<number, BoxStat>; selection: ReadonlySet<number>; onPick: (box: number, additive: boolean) => void; threshold: number; total: number;
}) {
  const paints = useBoxPaints();
  const slices = pieSlices(stats, 100, 100, 92, 46);
  const boxed = BOX_ORDER.reduce((s, b) => s + (stats.get(b)?.count ?? 0), 0);
  return (
    <div>
      <div style={CARD_HEAD}>By box · {total.toLocaleString()} names</div>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <svg width={200} height={200} viewBox="0 0 200 200" role="img" aria-label="Stocks by growth box">
          {slices.map((s) => (
            <path key={s.box} d={s.d} fill={paints(s.box).fill} fillRule="evenodd"
              stroke={selection.has(s.box) ? "var(--qf-accent)" : "var(--qf-surface)"} strokeWidth={selection.has(s.box) ? 3 : 2}
              style={{ cursor: "pointer" }} onClick={(e) => onPick(s.box, e.shiftKey || e.ctrlKey || e.metaKey)}>
              <title>{`${boxLabel(s.box)}: ${s.count}`}</title>
            </path>
          ))}
          <text x={100} y={98} textAnchor="middle" fill="var(--qf-text)" fontSize={22} fontWeight={700}>{boxed}</text>
          <text x={100} y={116} textAnchor="middle" fill="var(--qf-text-muted)" fontSize={11}>GR ≥ {threshold}</text>
        </svg>
        <div style={LEGEND}>
          {BOX_ORDER.map((b) => {
            const count = stats.get(b)?.count ?? 0;
            return (
              <button key={b} type="button" onClick={(e) => onPick(b, e.shiftKey || e.ctrlKey || e.metaKey)}
                style={{ ...LEGEND_ROW, opacity: selection.size && !selection.has(b) ? 0.45 : 1 }}>
                <span style={{ ...SWATCH, background: paints(b).fill }} />
                <span>{boxLabel(b)}</span>
                <span style={{ textAlign: "right" }}>{count}</span>
                <span style={{ textAlign: "right", color: "var(--qf-text-muted)" }}>{boxed ? Math.round((count / boxed) * 100) : 0}%</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const ROOT: CSSProperties = { flex: "0 0 auto", borderBottom: "3px solid var(--qf-surface)" };
const BAR: CSSProperties = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, padding: "5px 8px", fontSize: 12, borderBottom: "1px solid var(--qf-border)" };
const TOP: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 24, padding: "10px 12px", background: "var(--qf-surface)", alignItems: "flex-start" };
const LBL: CSSProperties = { color: "var(--qf-text-muted)" };
const SEP: CSSProperties = { width: 1, height: 18, background: "var(--qf-border)" };
const INPUT: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 5, background: "var(--qf-background)", color: "var(--qf-text)", padding: "2px 5px", font: "inherit", fontSize: 12 };
const BTN: CSSProperties = { ...INPUT, cursor: "pointer" };
const ON: CSSProperties = { borderColor: "var(--qf-accent)", color: "var(--qf-accent)" };
const HIST: CSSProperties = { color: "var(--qf-warn, #e3b341)" };
const SEG: CSSProperties = { display: "inline-flex", border: "1px solid var(--qf-border)", borderRadius: 6, overflow: "hidden" };
const SEG_BTN: CSSProperties = { border: 0, background: "transparent", padding: "3px 9px", font: "inherit", fontSize: 12, color: "var(--qf-text)", cursor: "pointer" };
const SEG_SEP: CSSProperties = { borderLeft: "1px solid var(--qf-border)" };
const SEG_ON: CSSProperties = { background: "var(--qf-accent)", color: "var(--qf-on-accent, #fff)" };
const CRUMB: CSSProperties = { ...INPUT, cursor: "pointer", color: "var(--qf-accent)" };
const NOTE: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 12 };
const CARD: CSSProperties = { fontSize: 12, color: "var(--qf-text-muted)", minWidth: 200, display: "grid", gap: 3 };
const CARD_HEAD: CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--qf-text-muted)", marginBottom: 6 };
const GRID: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(3, 88px) 10px 88px", gridTemplateRows: "repeat(3, 70px)", gap: 4 };
const SIDE: CSSProperties = { display: "grid", gridTemplateRows: "70px 70px", gap: 4, gridColumn: 5, gridRow: "1 / span 2" };
const CELL: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 6, cursor: "pointer", padding: "5px 7px", display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-start", font: "inherit", textAlign: "left" };
const LEGEND: CSSProperties = { display: "grid", gap: 2, fontSize: 12 };
const LEGEND_ROW: CSSProperties = { display: "grid", gridTemplateColumns: "14px 60px 36px 36px", gap: 6, alignItems: "center", border: 0, background: "transparent", color: "var(--qf-text)", font: "inherit", fontSize: 12, cursor: "pointer", padding: 0, textAlign: "left" };
const SWATCH: CSSProperties = { width: 10, height: 10, borderRadius: 2, display: "inline-block" };
